import { Form as InertiaForm } from '@inertiajs/react'
import { type ComponentProps, useState } from 'react'

import { Button } from '@/components/ui/button.tsx'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card.tsx'
import { Input } from '@/components/ui/input.tsx'
import { Label } from '@/components/ui/label.tsx'
import { Switch } from '@/components/ui/switch.tsx'
import { CategoryOptionType, TaxModuleOptionType, TaxType, TaxTypeOptionType } from './types.ts'

type FormProps = Omit<ComponentProps<typeof InertiaForm>, 'children'> & {
  tax: TaxType
  categories?: CategoryOptionType[]
  tax_types?: TaxTypeOptionType[]
  tax_modules?: TaxModuleOptionType[]
  submitText: string
}

export default function Form({ tax, categories = [], tax_types = [], tax_modules = [], submitText, ...formProps }: FormProps) {
  const [active, setActive] = useState(Boolean(tax.active))

  return (
    <InertiaForm
      transform={data => ({ tax: data })}
      className="contents"
      {...formProps}
    >
      {({ errors, processing }) => (
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle>Informations de la taxe</CardTitle>
            <CardDescription>Configurez le nom, la catégorie et le type de la taxe.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Nom</Label>
              <Input
                type="text"
                name="name"
                id="name"
                defaultValue={tax.name}
                placeholder="Nom de la taxe"
              />
              {errors.name && (
                <p className="text-sm font-medium text-destructive">
                  {Array.isArray(errors.name) ? errors.name.join(', ') : errors.name}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="category_id">Catégorie</Label>
              {categories.length > 0 ? (
                <select
                  name="category_id"
                  id="category_id"
                  defaultValue={tax.category_id || ""}
                  className="border-input focus-visible:border-ring focus-visible:ring-ring/50 h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:ring-[1px]"
                  required
                >
                  <option value="" disabled>Sélectionner une catégorie</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name} ({category.code})
                    </option>
                  ))}
                </select>
              ) : (
                <input type="hidden" name="category_id" defaultValue={tax.category_id || ""} />
              )}
              {errors.category_id && (
                <p className="text-sm font-medium text-destructive">
                  {Array.isArray(errors.category_id) ? errors.category_id.join(', ') : errors.category_id}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="tax_type_id">Type de taxe</Label>
              {tax_types.length > 0 ? (
                <select
                  name="tax_type_id"
                  id="tax_type_id"
                  defaultValue={tax.tax_type_id || ""}
                  className="border-input focus-visible:border-ring focus-visible:ring-ring/50 h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:ring-[1px]"
                  required
                >
                  <option value="" disabled>Sélectionner un type de taxe</option>
                  {tax_types.map((taxType) => (
                    <option key={taxType.id} value={taxType.id}>
                      {taxType.name} ({taxType.code})
                    </option>
                  ))}
                </select>
              ) : (
                <input type="hidden" name="tax_type_id" defaultValue={tax.tax_type_id || ""} />
              )}
              {errors.tax_type_id && (
                <p className="text-sm font-medium text-destructive">
                  {Array.isArray(errors.tax_type_id) ? errors.tax_type_id.join(', ') : errors.tax_type_id}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="tax_module_id">Module fiscal</Label>
              {tax_modules.length > 0 ? (
                <select
                  name="tax_module_id"
                  id="tax_module_id"
                  defaultValue={tax.tax_module_id || ""}
                  className="border-input focus-visible:border-ring focus-visible:ring-ring/50 h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:ring-[1px]"
                >
                  <option value="">Sélectionner un module</option>
                  {tax_modules.map((mod) => (
                    <option key={mod.id} value={mod.id}>
                      {mod.name} ({mod.code})
                    </option>
                  ))}
                </select>
              ) : (
                <input type="hidden" name="tax_module_id" defaultValue={tax.tax_module_id || ""} />
              )}
              {errors.tax_module_id && (
                <p className="text-sm font-medium text-destructive">
                  {Array.isArray(errors.tax_module_id) ? errors.tax_module_id.join(', ') : errors.tax_module_id}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="active">Statut</Label>
              <input type="hidden" name="active" value={active ? '1' : '0'} />
              <div className="flex items-center gap-3">
                <Switch
                  id="active"
                  checked={active}
                  onCheckedChange={setActive}
                  aria-label="Basculer le statut actif"
                />
                <span className="text-sm text-muted-foreground">
                  {active ? 'Activé' : 'Désactivé'}
                </span>
              </div>
              {errors.active && (
                <p className="text-sm font-medium text-destructive">
                  {Array.isArray(errors.active) ? errors.active.join(', ') : errors.active}
                </p>
              )}
            </div>

            <div className="flex justify-end">
              <Button type="submit" disabled={processing}>
                {submitText}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </InertiaForm>
  )
}
